import { createRouter, createWebHistory } from 'vue-router';
import EventBus from '@/bus';
import store from '@/store';
import sessionManager from '@/store/modules/session/sessionManager';
import * as types from '@/store/mutationTypes';
import {
  APPEARANCE_SET_PREFERENCES_VIEW,
  APPEARANCE_SET_IS_PUBLIC_VIEW_MODE,
  APPEARANCE_SET_IS_ON_AHA_PAGE,
  AUTHORISATION_SET_VIEW_TYPE
} from '@/store/mutationTypes';
import { VIEW_TYPES, ROLES } from '@/constants';
import isPagePathVisible from './isPagePathVisible';

// Lazy load components - only load them when the route is visited.
const LoginCallback = () => import('@/components/views/LoginCallback');
const Dashboard = () => import('@/components/views/dashboard/Dashboard');
const Page = () => import('@/components/views/Page');
const FeedbackPortal = () => import('@/components/views/feedback/FeedbackPortal');
const AhaLogin = () => import('@/components/views/feedback/AhaLogin');
const TempPage = () => import('@/components/views/TempPage');
const Context = () => import('@/components/views/preferences/Context');
const ContentAdmin = () => import('@/components/views/preferences/ContentAdmin');
const GroupAdmin = () => import('@/components/views/preferences/GroupAdmin');
const AuditLog = () => import('@/components/views/preferences/AuditLog');
const ReportDashboard = () => import('@/components/views/preferences/ReportDashboard');
const AssuranceIntegration = () => import('@/components/views/preferences/AssuranceIntegration');
const ContentRegister = () => import('@/components/views/preferences/ContentRegister');
const DocumentRegister = () => import('@/components/views/preferences/DocumentRegister');
const UserManagement = () => import('@/components/views/preferences/UserManagement');
const AccessDenied = () => import('@/components/views/AccessDenied');

export const routes = [
  {
    path: '/login/callback/idp',
    name: 'LoginCallback',
    component: LoginCallback
  },
  {
    path: '/',
    name: 'Dashboard',
    component: Dashboard,
    meta: { permission: 1 }
  },
  {
    path: '/module/:moduleId/page/:pagePath',
    name: 'Page',
    component: Page,
    meta: { permission: 1 }
  },
  {
    path: '/tempmodule/:moduleId/temppage/:pagePath',
    name: 'TempPage',
    component: TempPage,
    meta: { permission: 1 }
  },
  {
    path: '/preferences/context',
    name: 'Context',
    component: Context,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/contentAdmin',
    name: 'ContentAdmin',
    component: ContentAdmin,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/groupAdmin/:tab?',
    name: 'GroupAdmin',
    component: GroupAdmin,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/auditLog',
    name: 'AuditLog',
    component: AuditLog,
    meta: { permission: 2 }
  },
  {
    path: '/feedback',
    name: 'FeedbackPortal',
    component: FeedbackPortal,
    meta: { permission: 1 }
  },
  {
    path: '/aha-login',
    name: 'AhaLogin',
    component: AhaLogin
  },
  {
    path: '/preferences/reportDashboard',
    name: 'ReportDashboard',
    component: ReportDashboard,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/assuranceIntegration',
    name: 'AssuranceIntegration',
    component: AssuranceIntegration,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/contentRegister',
    name: 'ContentRegister',
    component: ContentRegister,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/documentRegister',
    name: 'DocumentRegister',
    component: DocumentRegister,
    meta: { permission: 2 }
  },
  {
    path: '/preferences/userManagement',
    name: 'UserManagement',
    component: UserManagement,
    meta: { permission: 2 }
  },
  {
    path: '/access-denied',
    name: 'AccessDenied',
    component: AccessDenied
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
    meta: { permission: 0 }
  }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

// For each route redirect, check if the `to` route requires authentication.
// If the user is not logged in and the route requires authentication then redirect
// to login screen.

const ADMIN_ROUTE_NAMES = [
  'Settings',
  'Context',
  'ContentAdmin',
  'GroupAdmin',
  'AuditLog',
  'ReportDashboard',
  'AssuranceIntegration',
  'ContentRegister',
  'DocumentRegister',
  'UserManagement'
];

const HUB_REDIRECT_EXCLUDED_PATHS = [
  '/login',
  '/login/callback',
  '/login/callback/hub',
  '/login/callback/idp',
  '/logout/callback/hub'
];

const restoreAdminRole = (routeName) => {
  if (!ADMIN_ROUTE_NAMES.includes(routeName)) return;

  const originalAdminRole = parseInt(sessionManager.getOriginalAdminRoleFromStorage(), 10);
  const currentUserRole = parseInt(sessionManager.getUserRoleFromStorage(), 10);

  if (originalAdminRole > ROLES.CLIENT_USER && currentUserRole !== originalAdminRole) {
    store.dispatch('authorisation/saveUserRole', originalAdminRole);
  }
};

const needsSupportRoleRefresh = (to) =>
  !HUB_REDIRECT_EXCLUDED_PATHS.includes(to.path) &&
  store.getters['session/isSupportMode'] &&
  store.getters['hub-session/isLoggedIn'] &&
  !store.getters['authorisation/isLoggedIn'];

const beforeEachRoute = async (to, _from, next) => {
  const isFirstLoad = _from.name === undefined;
  const isProtectedFirstLoad = isFirstLoad && !HUB_REDIRECT_EXCLUDED_PATHS.includes(to.path);

  if (isProtectedFirstLoad) {
    localStorage.setItem('redirect', to.path);
  }

  store.commit(APPEARANCE_SET_IS_PUBLIC_VIEW_MODE, false);
  store.commit(APPEARANCE_SET_IS_ON_AHA_PAGE, false);

  if (to.name === 'AhaLogin') {
    store.commit(APPEARANCE_SET_IS_ON_AHA_PAGE, true);
  }

  document.documentElement.scrollTop = 0;
  if (store.state.session.viewType === VIEW_TYPES.CLIENT) {
    const isPathVisible = isPagePathVisible(
      to.params.moduleId,
      to.params.pagePath,
      store.state.pageVisibilities.pages
    );

    // TODO: Investigate on this, it probably not be used anymore.
    if (!isPathVisible) {
      EventBus.$emit('notVisible');
      return;
    }
  }

  const isAdminRoute = ADMIN_ROUTE_NAMES.includes(to.name);

  // Change the side nav view dependent on if preferences are need to be shown.
  if (isAdminRoute) {
    store.commit(APPEARANCE_SET_PREFERENCES_VIEW, true);
  } else {
    store.commit(APPEARANCE_SET_PREFERENCES_VIEW, false);
  }

  // A target switch clears the cached role while the support hub session stays valid.
  if (needsSupportRoleRefresh(to)) {
    await store.dispatch('session/refreshSupportRole');
  }

  if (store.getters['authorisation/isLoggedIn']) {
    const permission = to.meta.permission ? to.meta.permission : 4;

    // If the user has admin privileges (id_original_adminRole > 1) but in client mode (id_userRole = 1), switch them to admin mode automatically when user hits an admin route.
    restoreAdminRole(to.name);

    if (isAdminRoute) {
      if (to.name === 'UserManagement' && !store.getters['hub/isEnabled']) {
        next('/');
      } else if (store.getters['authorisation/isAllowedTo'](permission)) {
        next();
      } else {
        next('/');
      }
    } else if (
      to.name !== 'login' &&
      (permission >= 0 || store.getters['authorisation/isAllowedTo'](permission))
    ) {
      next();
    } else {
      next('/');
    }
  } else if (
    to.name === 'login' ||
    to.name === 'LoginCallback' ||
    to.name === 'login/callback' ||
    to.name === 'login/callback/hub' ||
    to.name === 'logout/callback/hub' ||
    to.name === 'AhaLogin' ||
    to.name === 'AccessDenied'
  ) {
    next();
  } else if (to.name !== 'Page') {
    next('/login');
  } else {
    const isPagePublic = await store.dispatch('authorisation/isPagePublic', {
      moduleId: to.params.moduleId,
      pagePath: to.params.pagePath
    });

    if (isPagePublic) {
      // Allow public pages to go through
      store.commit(APPEARANCE_SET_IS_PUBLIC_VIEW_MODE, true);
      store.commit(AUTHORISATION_SET_VIEW_TYPE, VIEW_TYPES.CLIENT);
      store.dispatch('authorisation/saveUserRole', ROLES.CLIENT_USER);
      store.commit(types.APP_SET_INITIALISED);
      next();
    } else {
      localStorage.setItem('id_returnroute', window.location.pathname);
      next('/login');
    }
  }
};

export default router;

export { beforeEachRoute };
